/**
 * AuthGate — the boot guard at the root of authenticated routes. On mount it
 * attempts a silent refresh (csrf → refresh); on success it renders the protected
 * shell and, if a deep link was stashed before a prior login redirect, navigates
 * there. On a missing session it stashes the current path and redirects the
 * browser to centralized login. A session that ENDED (`revoked`,
 * `misconfigured`) is shown, never redirected past (H §4.5).
 *
 * Once authenticated, a principal the reseller gate refuses (H §4.4 — not a
 * `journix-reseller` / `RESELLER` token naming its reseller) never renders the
 * shell at all: it renders `NoAccess` instead of the `<Outlet/>`.
 *
 * The `auth/*` routes (return, error, signed-out, session-expired) are registered
 * as siblings of this gate in `router.tsx`, not children of it, so they stay
 * reachable for any principal.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bootRefresh, popDeepLink, redirectToCentralizedLogin } from './silentRefresh';
import { getAccessToken } from './authStore';
import { useAuth } from './authHooks';
import { SessionExpired } from '@/routes/SessionExpired';
import { SessionEnded } from '@/routes/SessionEnded';
import { NoAccess } from '@/routes/NoAccess';

type GateState = 'booting' | 'authed' | 'redirecting' | 'revoked' | 'misconfigured';

export function AuthGate(): ReactNode {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthed, sessionEnd, operator } = useAuth();
  const [state, setState] = useState<GateState>(() => (getAccessToken() ? 'authed' : 'booting'));
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (getAccessToken()) {
      setState('authed');
      return;
    }

    void (async () => {
      const result = await bootRefresh();
      if (result === 'ok') {
        // The store already holds the token and its expiry — publish, don't re-set.
        setAuthed();
        setState('authed');
        // Restore a stashed deep link (set before the centralized-login round-trip).
        const deep = popDeepLink();
        if (deep && deep !== location.pathname + location.search) {
          navigate(deep, { replace: true });
        }
      } else if (result === 'signin') {
        setState('redirecting');
        redirectToCentralizedLogin(location.pathname + location.search);
      } else {
        setState(result);
      }
    })();
  }, [location.pathname, location.search, navigate, setAuthed]);

  // A session that ended at runtime flips the AuthContext flag from the axios layer; every
  // authenticated screen mounts under this gate's <Outlet/>, so rendering the interstitial
  // here makes it global with no navigation from non-React code.
  // A boot refresh that ended the session is shown from the gate's own state as well.
  const ended =
    sessionEnd === 'revoked' || sessionEnd === 'misconfigured'
      ? sessionEnd
      : state === 'revoked' || state === 'misconfigured'
        ? state
        : null;
  if (ended) {
    return <SessionEnded reason={ended} />;
  }
  if (sessionEnd === 'expired') {
    return <SessionExpired />;
  }

  if (state === 'authed') {
    // A principal the gate refuses never renders the shell — checked once here, above
    // every route.
    if (operator === null) {
      return <NoAccess />;
    }
    return <Outlet />;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '0.5rem',
      }}
    >
      <p>{state === 'redirecting' ? t('auth.redirecting') : t('auth.loading')}</p>
    </div>
  );
}
